import { Stage } from 'konva/lib/Stage';
import { 
	MAX_ZOOM_LIMIT, 
	MIN_ZOOM_LIMIT, 
	PANEL_ENERGY_COLORS, 
	IRRADIANCE_UPRATE_RATE,
	FACET_HIGHLIGHT_OFF_COLOR,
	SEMI_TRANPARENT_BLACK_PANEL, 
	FULL_TRANPARENT_BLACK_PANEL
} from '../../../constants';
import { getDeratedValue, getPercentage } from '../../../util';
import Konva from 'konva';
import { getBaseRasterImage, KonvaGroupNames } from './konvaUtil';

function transformLatLngToRasterPoints(latitude: Lat, longitude: Lng, transformData: {
	boundingBox: BoundingBox,
	image: {
		width: number;
		height: number;
	}
}): {
	x: Lng,
	y: Lat
} {

	// boundingBox [minLong, minLat, maxLong, maxLat];
	const { sw, ne } = transformData.boundingBox;
	const { width: imageWidth, height: imageHeight } = transformData.image;

	const bboxWidth = ne.longitude - sw.longitude;
	const bboxHeight = ne.latitude - sw.latitude;
	const widthPerPixel = (longitude - sw.longitude) / bboxWidth;
	const heightPerPixel = (latitude - sw.latitude) / bboxHeight;

	const x = imageWidth * widthPerPixel;
	const y = imageHeight * (1 - heightPerPixel);
	// console.log('center',x,y);
	return {
		x, y
	};
}

function degToRad(angle: number) {
	return angle / 180 * Math.PI;
}

function getCenter(topLeft: Pos, dim: Dimensions) {
	const angleRad = degToRad(0);
	return {
		x:
			topLeft.x +
			dim.width / 2 * Math.cos(angleRad) +
			dim.height / 2 * Math.sin(-angleRad),
		y:
			topLeft.y +
			dim.height / 2 * Math.cos(angleRad) +
			dim.width / 2 * Math.sin(angleRad)
	};
}

function rotateAroundPoint(topLeft: Pos, dim: Dimensions, deltaDeg: number, point: Pos) {
	const angleRad = degToRad(deltaDeg);
	const x = Math.round(
		point.x +
		(topLeft.x - point.x) * Math.cos(angleRad) -
		(topLeft.y - point.y) * Math.sin(angleRad)
	);
	const y = Math.round(
		point.y +
		(topLeft.x - point.x) * Math.sin(angleRad) +
		(topLeft.y - point.y) * Math.cos(angleRad)
	);
	return {
		x,
		y
	};
}

export function rotateAroundCenter(topLeft: Pos, dim: Dimensions, deltaDeg: number) {
	const center = getCenter(topLeft, dim);
	return rotateAroundPoint(topLeft, dim, deltaDeg, center);
}

export function getSolarEnergyDetails(panels: RasterSolarPanel[], panelCapacityWatts: number, selectedPanels: string[], energyDerateRate: number) {
	let totalEnergy = 0, totalIrradiance = 0;
	for (let i = 0; i < panels.length; i++) {
		if (!selectedPanels.includes(panels[i].id)) continue;
		totalEnergy += panels[i].yearlyEnergyDcKwh;
		totalIrradiance += panels[i].irradiance;
	}
	// const offset = Math.round((energy.energy/energy.billEnergy)*100);
	return {
		energy: totalEnergy,
		deratedEnergy: getDeratedValue(totalEnergy, energyDerateRate),
		totalNoOfPanelsSelectedByUser: selectedPanels.length,
		panelCapacityWatts,
		selectedPanels,
		deratedBy: energyDerateRate,
		irradiance: totalIrradiance,
		upratedIrradiance: totalIrradiance + getPercentage(totalIrradiance, IRRADIANCE_UPRATE_RATE),
	};
}

export function getOffsetIndex(panels: RasterSolarPanel[], billEnergy: number, energyDerateRate: number) {
	let offset, totalEnergy = 0;
	let offset120;
	let offset150;
	for (let i = 0; i < panels.length; i++) {
		// totalEnergy += panels[i].yearlyEnergyDcKwh;
		totalEnergy += getDeratedValue(panels[i].yearlyEnergyDcKwh, energyDerateRate);
		offset = Math.round((totalEnergy / billEnergy) * 100);
		if (offset >= 120 && !offset120) {
			offset120 = i;
		}
		if (offset >= 150 && !offset150) {
			offset150 = i;
		}
	}

	return ({
		offset120,
		offset150
	});
}

export function detectPanelColor(energyValue: number) {
	// color-corrdinate: 500-425 red, 424-375 orange, 375-300 yellow,
	// panels under 300 remove them
	// && energyValue <= 500
	if (energyValue > 678) {
		return PANEL_ENERGY_COLORS.HIGH;
	}
	if (energyValue > 600 && energyValue <= 678) {
		return PANEL_ENERGY_COLORS.MEDIUM;
	}

	return PANEL_ENERGY_COLORS.LOW;

}

export function getAllKonvaGroups(konvaRef: Stage) {
	return konvaRef.find('Group').filter(n => !(n instanceof Konva.Transformer)) as Konva.Group[];
}

export function disableDraggingInKonvaGroups(groups: Konva.Group[]) {
	groups?.forEach(g => {
		g.draggable(false);
	});
}

export function deleteAllFacetsFromPanelDrawer(konvaRef: Konva.Stage) {
	getAllKonvaGroups(konvaRef)?.forEach((g => {
		if (g.attrs['name'] === 'panel-drawer') {
			[...(g?.children || [])].forEach((c) => {
				if (c instanceof Konva.Image) return;
				c.remove();
			});
		}
	}));
	konvaRef.batchDraw();
}

export function getPanelDrawerGroup(konvaRef: Konva.Stage) {
	return getAllKonvaGroups(konvaRef).filter(g => g.hasName('panel-drawer'))[0];
}

export function checkPanelDrawerHasUnfinalizedFacets(konvaRef: Konva.Stage) {
	const panelDrawerGroup = getAllKonvaGroups(konvaRef).filter(g => g.hasName('panel-drawer'))[0];
	return !!panelDrawerGroup?.children && panelDrawerGroup.children.length > 1;
}

export function getKonvaGroupByName(konvaRef: Konva.Stage, name: string) {
	return konvaRef.find('Group').filter(node => node.hasName(name))[0] as Konva.Group;
}

export function normalizeAngle(angle: number) {
	return (angle % 360 + 360) % 360;
}

/** Rotates a scaled stage-space vector by `angleDeg` degrees. */
export function rotateVectorDeg(v: Vector2d, angleDeg: number): Vector2d {
	const rad = angleDeg * Math.PI / 180;
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	return { x: v.x * cos - v.y * sin, y: v.x * sin + v.y * cos };
}

/** Converts a point in the stage's parent/container pixel space into the stage's local content space,
 *  accounting for its current position, scale and rotation. */
export function getLocalPoint(stage: Konva.Stage, point: Vector2d): Vector2d {
	return stage.getAbsoluteTransform().copy().invert().point(point);
}

/** Repositions the stage so that `pivotLocal` (a point in the stage's local content space) stays
 *  anchored at `pivotScreen` (stage parent/container pixel space) after rotating to `rotationDeg`. */
export function rotateStageAroundPivot(
	stage: Konva.Stage,
	pivotLocal: Vector2d,
	pivotScreen: Vector2d,
	rotationDeg: number,
) {
	const scale = stage.scaleX();
	const offset = rotateVectorDeg({ x: pivotLocal.x * scale, y: pivotLocal.y * scale }, rotationDeg);
	stage.rotation(rotationDeg);
	stage.position({ x: pivotScreen.x - offset.x, y: pivotScreen.y - offset.y });
}

export function zoomAtAPosition(stage: Konva.Stage, position: Vector2d, type: 'in' | 'out', by = 1.2) {
	const oldScale = stage.scaleX();
	const scaleBy = type === 'in' ? by : 1 / by;
	const newScale = oldScale * scaleBy;

	if (newScale < MIN_ZOOM_LIMIT) return;
	if (newScale > MAX_ZOOM_LIMIT) return;

	// Rotation-aware: reduces to the old plain-arithmetic formula when stage.rotation() is 0.
	const rotation = stage.rotation();
	const localPoint = getLocalPoint(stage, position);
	const offset = rotateVectorDeg({ x: localPoint.x * newScale, y: localPoint.y * newScale }, rotation);

	const newPos = {
		x: position.x - offset.x,
		y: position.y - offset.y,
	};

	// if (Math.abs(newPos.x) > stage.width() || Math.abs(newPos.y) > stage.height()) return;
	// Apply scale here too, not just position — callers only dispatch the returned scaleBy to
	// Redux, which re-applies it to the live stage via the controlled scaleX/scaleY props on
	// the NEXT render. Rapid consecutive calls (holding/fast-clicking a zoom button, or a fast
	// wheel scroll) land before that render happens, so oldScale above would keep reading the
	// stale pre-zoom value on every call — each one computing its position offset against the
	// wrong scale while the actual rendered scale barely changes, since every intermediate
	// zoomBy gets stomped by the next render's zoomLevel instead of compounding. The result:
	// position drifts far out of sync with the scale that actually lands, visibly uncovering
	// an edge of the canvas. Setting it immediately keeps every call in this sequence reading
	// the true current scale, so they compound and land in sync.
	stage.scale({ x: newScale, y: newScale });
	stage.position(newPos);
	return { scaleBy: newScale, position: newPos };
}

export function setZoomLevelOnStageFromCenter(stage: Konva.Stage) {
	const newPos= getNewPositonByApplyiingScaleFromCenter(stage);
	stage.position(newPos);

}

export function applyDisplacementBound(stage: Konva.Stage, pos: Vector2d){

	const scaleX = stage.scaleX();
	const scaleY = stage.scaleY();

	const raster = getBaseRasterImage(stage);
	if (!raster) {
		return pos;
	}

	// The raster is drawn at imgWidth x imgHeight inside the map layer, which has its own scale
	// (baseImageScale). Leaving it out puts the "center" in the wrong place, so the drag stopped while
	// far more than 1/4 of the image was still on the canvas.
	// e.g. layer scale 1.5, stage zoom 2: real width = imgWidth * 3, not imgWidth * 2.
	const layerScale = raster.getLayer()?.scale() ?? { x: 1, y: 1 };
	const stageWidth = raster.width() * layerScale.x * scaleX;
	const stageHeight = raster.height() * layerScale.y * scaleY;
	const halfSize = { x: stageWidth / 2, y: stageHeight / 2 };

	// The image's local origin (0,0) is its top-left corner, so its on-screen center is
	// pos + halfSize only while rotation is 0 — once rotated, that half-extent vector
	// needs to rotate with the stage too. Clamping the rotated center to the viewport
	// (instead of clamping pos directly) reduces to the exact original formula at
	// rotation 0 (rotateVectorDeg(halfSize, 0) === halfSize), so unrotated panning is
	// unaffected; it only changes behavior once the stage is actually rotated.
	const rotatedHalf = rotateVectorDeg(halfSize, stage.rotation());
	const center = { x: pos.x + rotatedHalf.x, y: pos.y + rotatedHalf.y };

	const clampedCenter = {
		x: Math.min(Math.max(center.x, 0), stage.width()),
		y: Math.min(Math.max(center.y, 0), stage.height()),
	};

	pos.x += clampedCenter.x - center.x;
	pos.y += clampedCenter.y - center.y;

	return pos;
}

export function resizePoints(points: number[], scale: Vector2d){
	return points.map((point, index) => index % 2 === 0 ? point * scale.x : point * scale.y);
}

export function getNewPositonByApplyiingScaleFromCenter(stage: Konva.Stage, konvaZoomScale?:number) {
	const stageScale = konvaZoomScale ?? stage.scaleX();
	const center = {
		x: stage.width() / 2,
		y: stage.height() / 2
	};

	const newPos = {
		x: center.x - center.x * stageScale,
		y: center.y - center.y * stageScale,
	};
	return newPos;
}

/**
 * 
 * @param polygon 
 * @returns perpendicular angle
 */
export function calculateAzimuthForNewlyCreatedFacet(polygon: number[][]) {
	const [x1, y1] = polygon[0];
	const [x2, y2] = polygon[1];

	const { x: Cx, y: Cy } = calculateCentroidOfPolygon(polygon.flat());

	const Mx = (x1 + x2) / 2, My = (y1 + y2) / 2; //midpoint;

	const theta = Math.atan2(y2 - y1, x2 - x1);

	const perpAngle1 = (theta + Math.PI / 2) * (180 / Math.PI);
	const perpAngle2 = (theta - Math.PI / 2) * (180 / Math.PI);

	// vector from the midpt to the centroid;
	const dx = Cx - Mx, dy = Cy - My;

	// Choose the perpendicular angle where the dot product is negative
	const dotProduct = dx * Math.cos(perpAngle1) + dy * Math.sin(perpAngle1);

	let angleDegrees = dotProduct < 0 ? perpAngle1 : perpAngle2;
	angleDegrees = (angleDegrees + 360) % 360;

	// angle with respect to the y-axis
	const finalAngle = (90 + angleDegrees) % 360;
	return finalAngle;
}

export function calculateArrowPoints(points: number[][], centroid: Vector2d) {

	const x1 = points[0][0];
	const y1 = points[0][1];
	const x2 = points[1][0];
	const y2 = points[1][1];

	// Calculate the midpoint of the side
	const midX = (x1 + x2) / 2;
	const midY = (y1 + y2) / 2;

	const theta = Math.atan2(y2 - y1, x2 - x1);

	const perpAngle1 = (theta + Math.PI / 2) * (180 / Math.PI);
	const perpAngle2 = (theta - Math.PI / 2) * (180 / Math.PI);

	// vector from the midpt to the centroid;

	// Calculate the direction vector of the side
	const dirX = x2 - x1;
	const dirY = y2 - y1;

	// Compute the outward normal vector (rotate the direction vector by 90 degrees)
	let outwardDirX = -dirY;
	let outwardDirY = dirX;

	// Normalize the normal vector
	const length = Math.sqrt(
		outwardDirX * outwardDirX + outwardDirY * outwardDirY
	);
	outwardDirX /= length;
	outwardDirY /= length;

	// Check if the normal is pointing outward using the dot product
	const vectorToCentroidX = centroid.x - midX;
	const vectorToCentroidY = centroid.y - midY;
	const dotProduct =
		outwardDirX * vectorToCentroidX + outwardDirY * vectorToCentroidY;

	let outwardAngle = perpAngle1, invardAngle = perpAngle1, invardDirX, invardDirY;
	// If the dot product is positive, the arrow is pointing inward, so reverse the direction
	if (dotProduct > 0) {
		invardDirX = outwardDirX;
		invardDirY = outwardDirY;
		outwardDirX = -outwardDirX;
		outwardDirY = -outwardDirY;
		outwardAngle = perpAngle2;
		invardAngle = perpAngle1;
	} else {
		invardDirX = -outwardDirX;
		invardDirY = -outwardDirY;
		outwardAngle = perpAngle1;
		invardAngle = perpAngle2;
	}

	// Scale the outward vector for the arrow size
	const arrowLength = 10, arrowStartPointOffset = 3;
	const padding = 1.5;

	const rectHeight = arrowLength + 2 * padding;
	const rectWidth = rectHeight;
	const rectCenterX = midX + outwardDirX * rectHeight / 2;
	const rectCenterY = midY + outwardDirY * rectHeight / 2;
	const rectAngle = Math.atan2(outwardDirY, outwardDirX) * (180 / Math.PI);

	const outwardArrowEndX = midX + outwardDirX * arrowLength;
	const outwardArrowEndY = midY + outwardDirY * arrowLength;
	const outwardArrowStartX = midX + outwardDirX * arrowStartPointOffset;
	const outwardArrowStartY = midY + outwardDirY * arrowStartPointOffset;

	const invardArrowEndX = midX + invardDirX * arrowLength;
	const invardArrowEndY = midY + invardDirY * arrowLength;

	outwardAngle = (outwardAngle + 360) % 360;
	invardAngle = (invardAngle + 360) % 360;

	//doubt: by this adjustments arrows are not aligning to the normal vector
	// const pp = 6;
	// if (outwardArrowEndY > midY) {
	// 	// midX -= 15
	// 	midY += pp;
	// } else {
	// 	// midX += 15;
	// 	midY -= pp;
	// }

	// return [[midX, midY], [arrowEndX, arrowEndY], (90 + angle) % 360];

	return {
		rectangle: {
			x: rectCenterX,
			y: rectCenterY,
			width: rectWidth,
			height: rectHeight,
			rotation: rectAngle,
			offsetX: rectWidth / 2,
			offsetY: rectHeight / 2
		},
		arrow:{
			'invardPoints': [[midX, midY], [invardArrowEndX, invardArrowEndY]],
			'outwardPoints': [[outwardArrowStartX, outwardArrowStartY], [outwardArrowEndX, outwardArrowEndY]],
			'angle': (90 + outwardAngle) % 360
		}
	
	};
}

export function calculateCentroidOfPolygon(points: number[]) {
	// Ensure that the first point is the same as the last point
	if (points[0] !== points[points.length - 2] || points[1] !== points[points.length - 1]) {
		points.push(points[0], points[1]); // Closing the polygon loop
	}

	const n = points.length / 2; // Number of points
	let area = 0;
	let centerX = 0;
	let centerY = 0;

	for (let i = 0; i < n - 1; i++) {
		const x0 = points[2 * i];
		const y0 = points[2 * i + 1];
		const x1 = points[2 * (i + 1)];
		const y1 = points[2 * (i + 1) + 1];

		const crossProduct = x0 * y1 - x1 * y0;

		area += crossProduct;
		centerX += (x0 + x1) * crossProduct;
		centerY += (y0 + y1) * crossProduct;
	}

	area *= 0.5;
	centerX *= (1 / (6 * area));
	centerY *= (1 / (6 * area));

	return { x: centerX, y: centerY };
}

export function getPolygonBoundingBox(points: number[][]) {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	for (const [x, y] of points) {
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}

	return {
		width: maxX - minX,
		height: maxY - minY,
	};
}


const rotatePoint = (x: number, y:number, angle:number, cx:number, cy:number) => {
	const radians = (angle * Math.PI) / 180;
	const cos = Math.cos(radians);
	const sin = Math.sin(radians);
	return [
		cos * (x - cx) - sin * (y - cy) + cx,
		sin * (x - cx) + cos * (y - cy) + cy
	];
};

export function convertLineCoordsToRectConfig(lineCoords: number[][], scale: Vector2d, angle:number): Partial<Konva.RectConfig>{

	const coords = lineCoords.slice(0,4);
	const xs = coords.map(point => point[0]);
	const ys = coords.map(point => point[1]);

	const cx = xs.reduce((sum, x)=>x+sum, 0)/4;
	const cy = ys.reduce((sum, y)=>y+sum, 0)/4;

	const rotatedCoords = coords.map(([x, y]) => rotatePoint(x, y, -angle, cx, cy));

	const rotatedxs = rotatedCoords.map(point => point[0]);
	const rotatedys = rotatedCoords.map(point => point[1]);

	const minX = Math.min(...rotatedxs);
	const maxX = Math.max(...rotatedxs);
	const minY = Math.min(...rotatedys);
	const maxY = Math.max(...rotatedys);

	const width = maxX - minX;
	const height = maxY - minY;

	const x = minX * scale.x + width / 2;
	const y = minY * scale.y + height / 2;

	return {x,y,width, height, scale, offset: {x: width/2,y: height/2}, rotation: angle};
}

/**
 * reset all tranformations got from transalation and while seleting panelGroup to align the handle to its azimuth
 * reset hightlight
 * @param stage 
 */
export function resetAllTransformations(stage: Konva.Stage, roofEditingEnabled= false) {
	
	const allFacetGroups = stage.find(`.${KonvaGroupNames.existingFacetsGroup}`) as Konva.Group[];

	// //detach transformer
	const transformers = stage.find('Transformer') as Konva.Transformer[];
	transformers.forEach((transformer) => {
		if (transformer?.getNodes()?.length) {
			transformer.setNodes([]);
		}
	});

	for (const facetGrp of allFacetGroups) {
		if (!facetGrp.children?.length) continue;

		for (const child of facetGrp.children) {
			const { rotation, fill } = child.attrs;
			const isFacetPolygon = child instanceof Konva.Line;
			const isPanelGroup = child instanceof Konva.Group;
			if (!roofEditingEnabled && isFacetPolygon && fill !== FACET_HIGHLIGHT_OFF_COLOR) {
				child.fill(FACET_HIGHLIGHT_OFF_COLOR);
			} else if (isPanelGroup && !(rotation == null)) {
				// panel group trandformations
				child.draggable(false);
				child.rotation(0);
				child.position({ x: 0, y: 0 });
				delete child.attrs.rotation;

				//individual panel polygon
				const panels = child.getChildren() as Konva.Line[];
				for (const line of panels) {
					if (line.attrs.rotation) line.rotation(0);
					if (line.attrs.fill === FULL_TRANPARENT_BLACK_PANEL) line.fill(SEMI_TRANPARENT_BLACK_PANEL);
				}
			}
		}
	}
}

export default transformLatLngToRasterPoints;

export function convertCoordsIntoKonvaCardinateSystem(coords: { x: number, y: number }, stage: Konva.Stage) {
	const stageRect = stage.getContent().getBoundingClientRect();
	const stageContent = stage.getContent();

	const stageBox = {
		top: stageRect.top,
		left: stageRect.left,
		scaleX: stageRect.width / stageContent.clientWidth || 1,
		scaleY: stageRect.height / stageContent.clientHeight || 1,
	};

	const x = (coords.x - stageBox.left) / stageBox.scaleX;
	const y = (coords.y - stageBox.top) / stageBox.scaleY;
	const transform = stage.getAbsoluteTransform().copy();
	const inv = transform.invert();
	return inv.point({ x: x, y: y });
}

export function calculateLineLength(point1: number[], point2: number[]) {
	const [x1, y1] = point1;
	const [x2, y2] = point2;
	const dx = x2 - x1;
	const dy = y2 - y1;
  
	return Math.hypot(dx, dy); 
}

export function getPolygonArea(points: number[]) {
	let area = 0;
	for (let i = 0; i < points.length; i += 2) {
		const x1 = points[i];
		const y1 = points[i + 1];
		const x2 = points[(i + 2) % points.length];
		const y2 = points[(i + 3) % points.length];
		area += x1 * y2 - x2 * y1;
	}
	return area / 2;
}

export function isPolygonCCW(points: number[]){
	const area = getPolygonArea(points);
	return area >0;
}

export function pointsToPolygon(points: number[]): number[][]{
	const polygon: number[][] = [];
	for (let i = 0; i + 1 < points.length; i += 2) {
		polygon.push([points[i], points[i + 1]]);
	}
	return polygon;
}
interface KonvaShapeRect {
	x: number;
	y: number;
	width: number;
	height: number;
	rotation: number;
}

function getKonvaShapeCenter(shape: KonvaShapeRect) {
	return {
		x: shape.x + (shape.width / 2) * Math.cos(shape.rotation) + (shape.height / 2) * Math.sin(-shape.rotation),
		y: shape.y + (shape.height / 2) * Math.cos(shape.rotation) + (shape.width / 2) * Math.sin(shape.rotation),
	};
}

function rotateKonvaShapeAroundPoint(shape: KonvaShapeRect, angleRad: number, point: { x: number; y: number }): KonvaShapeRect {
	const x = point.x + (shape.x - point.x) * Math.cos(angleRad) - (shape.y - point.y) * Math.sin(angleRad);
	const y = point.y + (shape.x - point.x) * Math.sin(angleRad) + (shape.y - point.y) * Math.cos(angleRad);
	return { ...shape, rotation: shape.rotation + angleRad, x, y };
}

export function rotateKonvaShapeAroundCenter(shape: KonvaShapeRect, deltaRad: number): KonvaShapeRect {
	const center = getKonvaShapeCenter(shape);
	return rotateKonvaShapeAroundPoint(shape, deltaRad, center);
}